import { useEffect } from "react";

// The Screen Wake Lock API isn't in TypeScript 4.9's DOM types. Just the parts
// used here.
interface ScreenWakeLock {
  readonly released: boolean;
  release(): Promise<void>;
}

type WakeLockNavigator = Navigator & {
  wakeLock: { request(type: "screen"): Promise<ScreenWakeLock> };
};

/**
 * Keeps the screen on while `active`, so a phone doesn't lock and cut off
 * playback in the browser.
 *
 * Uses the Screen Wake Lock API. The app used nosleep.js for this until it was
 * removed in April 2025. Browsers release the lock whenever the page is hidden,
 * so it's requested again each time the page becomes visible. Where the API is
 * missing, or a request is refused (battery saver, for example), the screen
 * just sleeps as usual.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) {
      return;
    }

    let sentinel: ScreenWakeLock | null = null;
    let cancelled = false;

    async function acquire(): Promise<void> {
      if (
        document.visibilityState !== "visible" ||
        (sentinel !== null && !sentinel.released)
      ) {
        return;
      }

      try {
        const lock = await (navigator as WakeLockNavigator).wakeLock.request(
          "screen"
        );

        if (cancelled) {
          void lock.release();
        } else {
          sentinel = lock;
        }
      } catch (e) {
        console.warn("Screen wake lock unavailable", e);
      }
    }

    function onVisibilityChange(): void {
      void acquire();
    }

    void acquire();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);

      if (sentinel !== null && !sentinel.released) {
        void sentinel.release();
      }
    };
  }, [active]);
}
